import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'

import type { RestaurantSummary } from '@/bindings/RestaurantSummary'
import { AccentScope } from '@/components/accent'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { type RestaurantListFilter, restaurantListQuery } from '@/features/restaurants/api'
import { accountName } from '@/features/back-office/account-name'
import { Pager } from '@/components/pager'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatDate, formatDateTime } from '@/lib/time'

export function RestaurantListPage() {
  const t = useT()
  const { page, filter } = useSearch({ from: '/platform-auth/restaurants' })
  const list = useQuery(restaurantListQuery(page, filter))

  const total = list.data?.total ?? 0

  return (
    <Page width="lg" title={t('restaurants.title')} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs filter={filter} />
        <Link to="/restaurants/new" className="bg-accent text-accent-fg hover:bg-accent-hover flex min-h-tap items-center rounded-lg px-4 font-medium">
          {t('restaurants.create')}
        </Link>
      </div>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t(filter === 'deletable' ? 'restaurants.emptyDeletable' : 'restaurants.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((restaurant) => (
              <li key={restaurant.id}>
                <RestaurantRow restaurant={restaurant} />
              </li>
            ))}
          </ul>
        ))}

      <Pager label={t('restaurants.pager')} page={page} total={total} link={(page) => ({ to: '/restaurants', search: filter ? { page, filter } : { page } })} />
    </Page>
  )
}

/**
 * K4: the whole list, or the deletion candidates — deactivated, no hold still
 * running, oldest deactivation first. The choice lives in the URL.
 */
function FilterTabs({ filter }: { filter: RestaurantListFilter | undefined }) {
  const t = useT()
  const tab = (active: boolean) =>
    cx(
      'flex min-h-tap items-center rounded-lg px-3 text-sm font-medium',
      active ? 'bg-accent-soft text-accent-ink' : 'text-muted hover:text-fg',
    )
  return (
    <nav aria-label={t('restaurants.filter')} className="flex gap-1">
      <Link to="/restaurants" search={{ page: 1 }} aria-current={filter === undefined ? 'page' : undefined} className={tab(filter === undefined)}>
        {t('restaurants.filterAll')}
      </Link>
      <Link
        to="/restaurants"
        search={{ page: 1, filter: 'deletable' }}
        aria-current={filter === 'deletable' ? 'page' : undefined}
        className={tab(filter === 'deletable')}
      >
        {t('restaurants.filterDeletable')}
      </Link>
    </nav>
  )
}

function RestaurantRow({ restaurant }: { restaurant: RestaurantSummary }) {
  const t = useT()
  const language = useLanguage()
  const superuser = restaurant.superuser
  const superuserLabel = superuser
    ? accountName(superuser)
    : undefined

  return (
    <AccentScope color={restaurant.color}>
      <Link
        to="/restaurants/$restaurantId"
        params={{ restaurantId: restaurant.id }}
        className="bg-surface border-border hover:bg-accent-soft flex items-center gap-3 rounded-xl border p-4"
      >
        <span aria-hidden className="bg-accent size-4 shrink-0 rounded-full" />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{restaurant.name}</span>
            {restaurant.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
            {!superuser && <Badge tone="warning">{t('restaurants.noSuperuser')}</Badge>}
          </span>
          <span className="text-muted flex flex-wrap gap-x-3 text-sm">
            <span>/{restaurant.slug}</span>
            <span>{t('restaurants.branchCount', { count: restaurant.branch_count })}</span>
            {superuserLabel && <span>{t('restaurants.superuserIs', { name: superuserLabel })}</span>}
            <span>{restaurant.last_order_date ? t('restaurants.lastOrder', { date: formatDate(restaurant.last_order_date, language) }) : t('restaurants.noOrders')}</span>
            {restaurant.deactivated_at && <span>{t('restaurants.deactivatedOn', { date: formatDateTime(restaurant.deactivated_at, language) })}</span>}
          </span>
        </span>
      </Link>
    </AccentScope>
  )
}
